import { logManager } from '../logs'
import { makeSafeForCSS } from '../utils'

/**
 * Renderer for managing where Tracks are rendered.
 */
export default function Renderer () {
  /*
   * Object holding reference to all tracks currently rendered and to where.
   *    Entry format: { containers, rendererId }
   *    `containers` is the array of HTMLElements where the track is rendered.
   *    `rendererId` is the ID used when creating DOM elements for this track.
   */
  const entries = {}

  /**
   * Renders this Track as a subelement of the specified element.
   * @method renderTrack
   * @param  {HTMLElement|String} container The DOM element to be rendered in,
   *    or a unique CSS selector for the DOM element.
   * @param  {String} [speakerId] The device ID to be used for audio output.
   */
  function renderTrack (track, container, speakerId) {
    const log = logManager.getLogger('Track', track.id)

    let element
    // If a string was provided, use it as a CSS selector to find the element.
    if (typeof container === 'string') {
      log.info(`Rendering track in element using selector: ${container}`)

      element = document.querySelector(container)
      if (!element) {
        log.error(`Unable to get container with selector: ${container}.`)
        return false
      }
    } else {
      log.info('Rendering track in provided HTMLElement.')
      element = container
    }

    const type = track.getState().kind
    // Create a unique rendering ID for this track.
    const rendererId = `${type}-${makeSafeForCSS(track.id)}`

    // Get the existing entry for this track.
    let entry = entries[track.id]
    if (entry) {
      if (entry.containers.findIndex(item => item.element === element) > -1) {
        // Already rendered in element.
        log.info('Failed to render track; already rendered in element.')
        return
      } else {
        // Rendering the track in a second element; no issue with that.
      }
    } else {
      // Create an empty entry; track is not rendered anywhere else.
      entry = { containers: [], rendererId }
    }

    // Element is a valid place to render track, so add it to the entry then render it.
    entry.containers.push(element)

    const renderer = document.createElement(type)
    renderer.id = rendererId
    renderer.style.height = '100%'
    renderer.style.width = '100%'

    const stream = track.getStream()
    try {
      renderer.srcObject = stream
    } catch (error) {
      log.debug('srcObject property not supported; reverting to createObjectURL.')
      // TODO: AdapterJS handles this, we should remove this.
      renderer.src = window.URL.createObjectURL(stream)
    }

    renderer.autoplay = 'true'

    if (type === 'video') {
      renderer.muted = 'true'
      // Needed for IOS autoplay.
      renderer.playsInline = 'true'
      // Needed for Android.
      renderer.play().catch(err => {
        log.debug(`Could not autoplay renderer #${renderer.id}: ${err.message}`)
      })
    }

    // Set speaker if it was provided and it's supported.
    if (speakerId && typeof renderer.setSinkId !== 'undefined') {
      // TODO: Better then/catch handling.
      renderer
        .setSinkId(speakerId)
        .then(() => {
          log.debug(`Set to use speaker: ${speakerId}.`)
        })
        .catch(error => {
          log.debug(`Could not set speaker to use ${speakerId}: ${error.message}`)
        })
    } else if (speakerId && typeof renderer.setSinkId === 'undefined') {
      log.info('Failed to set speaker; setSinkId not supported in this browser.')
    }

    element.appendChild(renderer)

    // Save the new/updated entry to the Renderer scope.
    entries[track.id] = entry
  }

  /**
   * Stop rendering this Track from the specified element.
   * @method unrenderTrack
   * @param  {string}      trackId   ID of the track to be unrendered.
   * @param  {HTMLElement} container The DOM element to be removed from, or
   *     a unique CSS selector for the DOM element.
   */
  function unrenderTrack (trackId, container) {
    const log = logManager.getLogger('Track', trackId)

    // Get the existing entry for this track.
    const entry = entries[trackId]
    if (!entry) {
      log.info('Failed to unrender track; not rendered anywhere.')
      return
    }

    let element
    // If a string was provided, use it as a CSS selector to find the element.
    if (typeof container === 'string') {
      log.info(`Unrendering track from element using selector: ${container}`)

      element = document.querySelector(container)
      if (!element) {
        log.error(`Unable to get container with selector: ${container}.`)
        return false
      }
    } else {
      log.info('Unrendering track from provided HTMLElement.')
      element = container
    }

    const index = entry.containers.findIndex(item => item === element)
    if (index === -1) {
      // Not rendered in element.
      log.info('Failed to unrender track; not rendered in element.')
      return
    }

    const renderer = element.querySelector(`#${entry.rendererId}`)

    if (renderer.srcObject) {
      renderer.srcObject = null
    } else if (renderer.src) {
      renderer.src = null
    }
    element.removeChild(renderer)

    entry.containers.splice(index, 1)
    // If this track isn't rendered anywhere anymore, remove the reference to it.
    if (entry.containers.length === 0) {
      delete entries[trackId]
    }
  }

  return {
    renderTrack,
    unrenderTrack
  }
}
